/**
 * Keeps a conversation looking read from the instant it is opened.
 *
 * Opening a chat clears its unread count on the server, but the client only
 * learns that when the next GET /conversations answers — seconds on a heavy
 * account — and any request already in flight answers with the pre-read
 * counts. Without this, going back to the list showed the chat still unread
 * until a later poll happened to win, which is the "wait tens of seconds and
 * it fixes itself" report.
 *
 * So the read is applied locally and the server's answer is reconciled
 * against it: a count that still reflects the pre-read state is a stale race
 * and loses, while a message that genuinely arrived after the read wins and
 * ends the suppression.
 */

export type ReadableConversation = {
  id: string;
  unread?: number;
  lastMessage: { timestamp: Date };
};

type ReadEntry = {
  readAt: number;
  /**
   * Last message known at read time. Null when the chat was opened before the
   * list had ever loaded (a deep link), and filled in on first sighting.
   */
  lastMessageAt: number | null;
};

export type ConversationReadTracker = {
  /** Record that the viewer has just opened `conversationId`. */
  markRead: (conversationId: string, lastMessageAt: number | null) => void;
  /**
   * Apply the local reads to a freshly fetched list. `selectedId` is the chat
   * currently on screen, which can never have anything unread in it.
   */
  reconcile: <T extends ReadableConversation>(
    incoming: T[],
    selectedId: string | null,
  ) => T[];
  /** Forget every local read (logout). */
  clear: () => void;
};

/**
 * How long a local read outranks the server. Long enough to cover a slow
 * /conversations round trip and the 30s poll behind it, short enough that a
 * read which never reached the server (failed request) doesn't hide real
 * messages for the rest of the session.
 */
const DEFAULT_SUPPRESSION_MS = 90000;

export const createConversationReadTracker = ({
  suppressionMs = DEFAULT_SUPPRESSION_MS,
  now = Date.now,
}: { suppressionMs?: number; now?: () => number } = {}): ConversationReadTracker => {
  const reads = new Map<string, ReadEntry>();

  return {
    markRead(conversationId, lastMessageAt) {
      reads.set(conversationId, { readAt: now(), lastMessageAt });
    },

    reconcile(incoming, selectedId) {
      if (reads.size === 0 && !selectedId) return incoming;

      const at = now();
      const reconciled = incoming.map(conv => {
        const lastMessageAt = conv.lastMessage.timestamp.getTime();

        // The chat is open on screen, so nothing in it is unread — its own
        // message poll is what clears the count server-side, and the two race.
        // Re-arm the read so going back doesn't flash the badge either.
        if (conv.id === selectedId) {
          reads.set(conv.id, { readAt: at, lastMessageAt });
          return conv.unread ? { ...conv, unread: 0 } : conv;
        }

        const entry = reads.get(conv.id);
        if (!entry) return conv;

        // First sighting since the read: whatever the server calls the last
        // message is what the viewer just looked at.
        if (entry.lastMessageAt === null) entry.lastMessageAt = lastMessageAt;

        const serverAgrees = !conv.unread;
        const arrivedSinceRead = lastMessageAt > entry.lastMessageAt;
        const expired = at - entry.readAt > suppressionMs;
        if (serverAgrees || arrivedSinceRead || expired) {
          reads.delete(conv.id);
          return conv;
        }
        return { ...conv, unread: 0 };
      });

      // Drop reads for conversations that stopped coming back in the list
      // (left, ended, deleted) so the map can't grow for a whole session.
      reads.forEach((entry, id) => {
        if (at - entry.readAt > suppressionMs) reads.delete(id);
      });

      return reconciled as typeof incoming;
    },

    clear() {
      reads.clear();
    },
  };
};
